/**
 * Company records behind a person's jobs, for the person panel's Experience
 * rows (epic 2026-09-25-chopbook-company-lists, sub-task 03): the company name
 * links to its page, and a job with no company can be linked by hand.
 *
 * Two ways a job reaches its record, resolved in one call:
 * - by value, the job's LinkedIn company id (00 D3), like "People we know here";
 * - by hand, the job's `company_ref` (0054) for a job with no company id.
 *
 * Logos of jobs linked by value come from `person-panel/useJobLogos` (released
 * for everyone); this hook presigns only the hand-linked ones, which exist only
 * where the company screens are visible.
 *
 * Only active where the company screens are visible (the `company-lists` page:
 * its flag, released on every stage 2026-10-20), so with the flag off the
 * person panel keeps plain company names.
 */
import type { CompanyRef } from "@nu-assist/contracts";
import { skipToken, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { createCompanyListsClient } from "../../clients/companyLists.client";
import { activeFlags } from "../../config/featureFlags";
import { usePreviewModeIfMounted } from "../PreviewProvider";
import { pageVisible } from "../useFeatureAccess";
import { COMPANY_LISTS_PAGE_ID } from "./paths";
import { COMPANY_LISTS_KEY, useLogoUrls } from "./queries";

const API_BASE = import.meta.env.VITE_API_URL ?? "https://api.local.nu-assist.com";
export const jobCompaniesClient = createCompanyListsClient({ apiBase: API_BASE });

export const JOB_COMPANIES_KEY = [...COMPANY_LISTS_KEY, "by-job"] as const;

export interface JobCompany extends CompanyRef {
  /** Presigned logo, for a job linked by hand (value-linked jobs use useJobLogos). */
  logoUrl?: string | null;
}

interface JobKeys {
  company_id?: string | null;
  company_ref?: string | null;
}

function uniqueSorted(values: ReadonlyArray<string | null | undefined>): string {
  return Array.from(new Set(values.filter((v): v is string => Boolean(v))))
    .sort()
    .join(",");
}

export function useJobCompanies(jobs: ReadonlyArray<JobKeys>): {
  /** Whether the company screens (and so the link action) are visible. */
  enabled: boolean;
  companyOf: (job: JobKeys) => JobCompany | undefined;
} {
  const enabled = pageVisible(
    activeFlags(),
    usePreviewModeIfMounted(),
    "linkedin",
    COMPANY_LISTS_PAGE_ID
  );
  // Keyed on the ids' content: the caller builds a new array every render.
  const linkedinKey = uniqueSorted(jobs.map((j) => j.company_id));
  const refKey = uniqueSorted(jobs.map((j) => j.company_ref));
  const refs = useQuery({
    queryKey: [...JOB_COMPANIES_KEY, linkedinKey, refKey],
    queryFn:
      enabled && (linkedinKey || refKey)
        ? () =>
            jobCompaniesClient.companiesByLinkedinIds(
              linkedinKey ? linkedinKey.split(",") : [],
              refKey ? refKey.split(",") : []
            )
        : skipToken,
    staleTime: 5 * 60 * 1000,
  });
  const refIds = useMemo(() => new Set(refKey ? refKey.split(",") : []), [refKey]);
  const logoUrls = useLogoUrls(
    jobCompaniesClient,
    (refs.data ?? []).filter((r) => refIds.has(r.company_id))
  );
  const maps = useMemo(() => {
    const byLinkedin = new Map<string, JobCompany>();
    const byId = new Map<string, JobCompany>();
    for (const r of refs.data ?? []) {
      const withLogo = {
        ...r,
        logoUrl: r.logo_s3_key ? (logoUrls.get(r.logo_s3_key) ?? null) : null,
      };
      if (r.linkedin_id) byLinkedin.set(r.linkedin_id, withLogo);
      byId.set(r.company_id, withLogo);
    }
    return { byLinkedin, byId };
  }, [refs.data, logoUrls]);
  const companyOf = useCallback(
    (job: JobKeys) =>
      job.company_id
        ? maps.byLinkedin.get(job.company_id)
        : job.company_ref
          ? maps.byId.get(job.company_ref)
          : undefined,
    [maps]
  );
  return { enabled, companyOf };
}
